import React, { useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { DobbyActionType } from "@services/admin/types";

// All available DobbyActionType values (keep in sync with backend)
const ALL_ACTION_TYPES: DobbyActionType[] = [
  "WebScrape",
  "EmailMoveInFolder",
  "Analysis",
  "Error",
  "EmailSend",
  "NoOp",
  "EmailRoute",
  "GptRequest",
  "ExtractDataFromEmail",
  "ExtractData",
  "SpreadsheetLookup",
  "StartAIAgent",
  "EndAIAgent",
  "ProcessShipmentEmail",
  "ExtractShipmentIdentifiers",
  "ExtractThreadContext",
  "CreateExtractedData",
  "UpdateShipmentWithLLM",
  "CreateShipment",
  "UpdateShipment",
  "MergeShipment",
  "CreateEmailSummariesForShipments",
  "CompleteShipmentEmailProcessing",
  "ShipmentEmailProcessingError",
  "emailAutomationPipelineRun",
];

export interface ActionLogFilters {
  selectedOrgId: string;
  selectedUserId: string;
  selectedTypes: DobbyActionType[];
  emailFilter: string;
  extractedDataFilter: string;
  derivedDataFilter: string;
  messageIdFilter: string;
  recurringWorkflowFilter: string;
  workflowFilter: string;
  reminderFilter: string;
  shipmentFeedFilter: string;
  startDate: string;
  endDate: string;
  errorsOnly: boolean;
  errorRegex: string;
  includeDescription: boolean;
  displayErrors: boolean;
}

export interface ActionLogFiltersReturn extends ActionLogFilters {
  // Setters
  setSelectedOrgId: (value: string) => void;
  setSelectedUserId: (value: string) => void;
  setSelectedTypes: (value: DobbyActionType[]) => void;
  setEmailFilter: (value: string) => void;
  setExtractedDataFilter: (value: string) => void;
  setDerivedDataFilter: (value: string) => void;
  setMessageIdFilter: (value: string) => void;
  setRecurringWorkflowFilter: (value: string) => void;
  setWorkflowFilter: (value: string) => void;
  setReminderFilter: (value: string) => void;
  setShipmentFeedFilter: (value: string) => void;
  setStartDate: (value: string) => void;
  setEndDate: (value: string) => void;
  setErrorsOnly: (value: boolean) => void;
  setErrorRegex: (value: string) => void;
  setIncludeDescription: (value: boolean) => void;
  setDisplayErrors: (value: boolean) => void;

  // Actions
  clearFilters: () => void;
  hasActiveFilters: boolean;

  // Utility methods
  setFiltersFromUrlParams: (searchParams: URLSearchParams) => void;
  getUrlSearchParams: () => URLSearchParams;
}

const initialFilters: ActionLogFilters = {
  selectedOrgId: "",
  selectedUserId: "",
  selectedTypes: [],
  emailFilter: "",
  extractedDataFilter: "",
  derivedDataFilter: "",
  messageIdFilter: "",
  recurringWorkflowFilter: "",
  workflowFilter: "",
  reminderFilter: "",
  shipmentFeedFilter: "",
  startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  endDate: "",
  errorsOnly: false,
  errorRegex: "",
  includeDescription: false,
  displayErrors: false,
};

export function useActionLogFilters(): ActionLogFiltersReturn {
  const [selectedOrgId, setSelectedOrgId] = useState(
    initialFilters.selectedOrgId
  );
  const [selectedUserId, setSelectedUserId] = useState(
    initialFilters.selectedUserId
  );
  const [selectedTypes, setSelectedTypes] = useState<DobbyActionType[]>(
    initialFilters.selectedTypes
  );
  const [emailFilter, setEmailFilter] = useState(initialFilters.emailFilter);
  const [extractedDataFilter, setExtractedDataFilter] = useState(
    initialFilters.extractedDataFilter
  );
  const [derivedDataFilter, setDerivedDataFilter] = useState(
    initialFilters.derivedDataFilter
  );
  const [messageIdFilter, setMessageIdFilter] = useState(
    initialFilters.messageIdFilter
  );
  const [recurringWorkflowFilter, setRecurringWorkflowFilter] = useState(
    initialFilters.recurringWorkflowFilter
  );
  const [workflowFilter, setWorkflowFilter] = useState(
    initialFilters.workflowFilter
  );
  const [reminderFilter, setReminderFilter] = useState(
    initialFilters.reminderFilter
  );
  const [shipmentFeedFilter, setShipmentFeedFilter] = useState(
    initialFilters.shipmentFeedFilter
  );
  const [startDate, setStartDate] = useState(initialFilters.startDate);
  const [endDate, setEndDate] = useState(initialFilters.endDate);
  const [errorsOnly, setErrorsOnly] = useState(initialFilters.errorsOnly);
  const [errorRegex, setErrorRegex] = useState(initialFilters.errorRegex);
  const [includeDescription, setIncludeDescription] = useState(
    initialFilters.includeDescription
  );
  const [displayErrors, setDisplayErrors] = useState(
    initialFilters.displayErrors
  );

  const clearFilters = useCallback(() => {
    setSelectedOrgId(initialFilters.selectedOrgId);
    setSelectedUserId(initialFilters.selectedUserId);
    setSelectedTypes(initialFilters.selectedTypes);
    setEmailFilter(initialFilters.emailFilter);
    setExtractedDataFilter(initialFilters.extractedDataFilter);
    setDerivedDataFilter(initialFilters.derivedDataFilter);
    setMessageIdFilter(initialFilters.messageIdFilter);
    setRecurringWorkflowFilter(initialFilters.recurringWorkflowFilter);
    setWorkflowFilter(initialFilters.workflowFilter);
    setReminderFilter(initialFilters.reminderFilter);
    setShipmentFeedFilter(initialFilters.shipmentFeedFilter);
    setStartDate(initialFilters.startDate);
    setEndDate(initialFilters.endDate);
    setErrorsOnly(initialFilters.errorsOnly);
    setErrorRegex(initialFilters.errorRegex);
    setIncludeDescription(initialFilters.includeDescription);
    setDisplayErrors(initialFilters.displayErrors);
  }, []);

  const hasActiveFilters =
    selectedOrgId !== "" ||
    selectedUserId !== "" ||
    selectedTypes.length > 0 ||
    emailFilter !== "" ||
    extractedDataFilter !== "" ||
    derivedDataFilter !== "" ||
    messageIdFilter !== "" ||
    recurringWorkflowFilter !== "" ||
    workflowFilter !== "" ||
    reminderFilter !== "" ||
    shipmentFeedFilter !== "" ||
    startDate !== "" ||
    endDate !== "" ||
    errorsOnly !== false ||
    errorRegex !== "" ||
    includeDescription !== false ||
    displayErrors !== false;

  const setFiltersFromUrlParams = useCallback(
    (searchParams: URLSearchParams) => {
      const initialEmailId = searchParams.get("emailId");
      const initialExtractedDataId = searchParams.get("extractedDataId");
      const initialDerivedDataId = searchParams.get("derivedDataId");
      const initialRecurringWorkflowId = searchParams.get(
        "recurringWorkflowId"
      );
      const initialWorkflowId = searchParams.get("workflowId");
      const initialReminderId = searchParams.get("reminderId");
      const initialShipmentFeedId = searchParams.get("shipmentFeedId");
      const initialTypes = searchParams.get("types");
      const initialErrorsOnly = searchParams.get("errorsOnly");
      const initialIncludeDescription = searchParams.get("includeDescription");
      const initialDisplayErrors = searchParams.get("displayErrorsInline");
      const initialOrgId = searchParams.get("orgId");
      const initialUserId = searchParams.get("userId");
      const initialStartDate = searchParams.get("startDate");
      const initialEndDate = searchParams.get("endDate");
      const initialErrorRegex = searchParams.get("errorRegex");
      const initialMessageId = searchParams.get("messageId");

      if (initialEmailId) setEmailFilter(initialEmailId);
      if (initialExtractedDataId)
        setExtractedDataFilter(initialExtractedDataId);
      if (initialDerivedDataId) setDerivedDataFilter(initialDerivedDataId);
      if (initialRecurringWorkflowId)
        setRecurringWorkflowFilter(initialRecurringWorkflowId);
      if (initialWorkflowId) setWorkflowFilter(initialWorkflowId);
      if (initialReminderId) setReminderFilter(initialReminderId);
      if (initialShipmentFeedId) setShipmentFeedFilter(initialShipmentFeedId);
      if (initialOrgId) setSelectedOrgId(initialOrgId);
      if (initialUserId) setSelectedUserId(initialUserId);
      if (initialStartDate) setStartDate(initialStartDate);
      if (initialEndDate) setEndDate(initialEndDate);
      if (initialErrorRegex) setErrorRegex(initialErrorRegex);
      if (initialMessageId) setMessageIdFilter(initialMessageId);

      if (initialTypes) {
        const typesArray = initialTypes
          .split(",")
          .filter((type) =>
            ALL_ACTION_TYPES.includes(type as DobbyActionType)
          ) as DobbyActionType[];
        setSelectedTypes(typesArray);
      }

      if (initialErrorsOnly === "true") {
        setErrorsOnly(true);
      }
      if (initialIncludeDescription === "true") {
        setIncludeDescription(true);
      }
      if (initialDisplayErrors === "true") {
        setDisplayErrors(true);
      }
    },
    []
  );

  const getUrlSearchParams = useCallback(() => {
    const searchParams = new URLSearchParams();

    if (selectedOrgId) searchParams.set("orgId", selectedOrgId);
    if (selectedUserId) searchParams.set("userId", selectedUserId);
    if (selectedTypes.length > 0)
      searchParams.set("types", selectedTypes.join(","));
    if (emailFilter) searchParams.set("emailId", emailFilter);
    if (extractedDataFilter)
      searchParams.set("extractedDataId", extractedDataFilter);
    if (derivedDataFilter) searchParams.set("derivedDataId", derivedDataFilter);
    if (messageIdFilter) searchParams.set("messageId", messageIdFilter);
    if (recurringWorkflowFilter)
      searchParams.set("recurringWorkflowId", recurringWorkflowFilter);
    if (workflowFilter) searchParams.set("workflowId", workflowFilter);
    if (reminderFilter) searchParams.set("reminderId", reminderFilter);
    if (shipmentFeedFilter) searchParams.set("shipmentFeedId", shipmentFeedFilter);
    if (startDate) searchParams.set("startDate", startDate);
    if (endDate) searchParams.set("endDate", endDate);
    if (errorsOnly) searchParams.set("errorsOnly", "true");
    if (errorRegex) searchParams.set("errorRegex", errorRegex);
    if (includeDescription) searchParams.set("includeDescription", "true");
    if (displayErrors) searchParams.set("displayErrorsInline", "true");

    return searchParams;
  }, [
    selectedOrgId,
    selectedUserId,
    selectedTypes,
    emailFilter,
    extractedDataFilter,
    derivedDataFilter,
    messageIdFilter,
    recurringWorkflowFilter,
    workflowFilter,
    reminderFilter,
    shipmentFeedFilter,
    startDate,
    endDate,
    errorsOnly,
    errorRegex,
    includeDescription,
    displayErrors,
  ]);

  return {
    // Filter values
    selectedOrgId,
    selectedUserId,
    selectedTypes,
    emailFilter,
    extractedDataFilter,
    derivedDataFilter,
    messageIdFilter,
    recurringWorkflowFilter,
    workflowFilter,
    reminderFilter,
    shipmentFeedFilter,
    startDate,
    endDate,
    errorsOnly,
    errorRegex,
    includeDescription,
    displayErrors,

    // Setters
    setSelectedOrgId,
    setSelectedUserId,
    setSelectedTypes,
    setEmailFilter,
    setExtractedDataFilter,
    setDerivedDataFilter,
    setMessageIdFilter,
    setRecurringWorkflowFilter,
    setWorkflowFilter,
    setReminderFilter,
    setShipmentFeedFilter,
    setStartDate,
    setEndDate,
    setErrorsOnly,
    setErrorRegex,
    setIncludeDescription,
    setDisplayErrors,

    // Actions
    clearFilters,
    hasActiveFilters,

    // Utility methods
    setFiltersFromUrlParams,
    getUrlSearchParams,
  };
}

interface OrganizationInfo {
  id: string;
  name: string;
}

interface UserInfo {
  id: string;
  email: string;
}

interface ActionLogFiltersProps {
  filters: ActionLogFiltersReturn;
  viewMode: "grouped" | "list";
  organizations: OrganizationInfo[];
  users: UserInfo[];
  total: number;
  actionLogsCount: number;
  groupedLogsCount?: number;
  handleSearch: () => void;
}

export function ActionLogFilters({
  filters,
  viewMode,
  organizations,
  users,
  total,
  actionLogsCount,
  groupedLogsCount,
  handleSearch,
}: ActionLogFiltersProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>Organization</InputLabel>
              <Select
                value={filters.selectedOrgId}
                onChange={(e) => filters.setSelectedOrgId(e.target.value)}
                label="Organization"
              >
                <MenuItem value="">All Organizations</MenuItem>
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>User</InputLabel>
              <Select
                value={filters.selectedUserId}
                onChange={(e) => filters.setSelectedUserId(e.target.value)}
                label="User"
              >
                <MenuItem value="">All Users</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>Types</InputLabel>
              <Select
                multiple
                value={filters.selectedTypes}
                onChange={(e) => {
                  const value = e.target.value;
                  filters.setSelectedTypes(
                    typeof value === "string"
                      ? (value.split(",") as DobbyActionType[])
                      : (value as DobbyActionType[])
                  );
                }}
                label="Types"
                renderValue={(selected) => {
                  if (selected.length === 0) return "All Types";
                  if (selected.length === 1) return selected[0];
                  return `${selected.length} types selected`;
                }}
              >
                {ALL_ACTION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={filters.selectedTypes.includes(type)} />
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Start Date"
              type="datetime-local"
              value={filters.startDate}
              onChange={(e) => filters.setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
            />

            <TextField
              size="small"
              label="End Date"
              type="datetime-local"
              value={filters.endDate}
              onChange={(e) => filters.setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <TextField
              size="small"
              label="Email ID"
              value={filters.emailFilter}
              onChange={(e) => filters.setEmailFilter(e.target.value)}
              sx={{ width: 200 }}
            />

            <TextField
              size="small"
              label="Extracted Data ID"
              value={filters.extractedDataFilter}
              onChange={(e) => filters.setExtractedDataFilter(e.target.value)}
              sx={{ width: 200 }}
            />

            <TextField
              size="small"
              label="Derived Data ID"
              value={filters.derivedDataFilter}
              onChange={(e) => filters.setDerivedDataFilter(e.target.value)}
              sx={{ width: 200 }}
            />

            <TextField
              size="small"
              label="Message ID"
              value={filters.messageIdFilter}
              onChange={(e) => filters.setMessageIdFilter(e.target.value)}
              sx={{ width: 200 }}
            />

            <TextField
              size="small"
              label="Recurring Workflow ID"
              value={filters.recurringWorkflowFilter}
              onChange={(e) =>
                filters.setRecurringWorkflowFilter(e.target.value)
              }
              sx={{
                width: 200,
                "& .MuiOutlinedInput-root":
                  filters.recurringWorkflowFilter &&
                  searchParams.get("recurringWorkflowId")
                    ? {
                        "& fieldset": {
                          borderColor: "primary.main",
                        },
                      }
                    : {},
              }}
            />

            <TextField
              size="small"
              label="Workflow ID"
              value={filters.workflowFilter}
              onChange={(e) => filters.setWorkflowFilter(e.target.value)}
              sx={{
                width: 200,
                "& .MuiOutlinedInput-root":
                  filters.workflowFilter && searchParams.get("workflowId")
                    ? {
                        "& fieldset": {
                          borderColor: "primary.main",
                        },
                      }
                    : {},
              }}
            />

            <TextField
              size="small"
              label="Shipment Feed ID"
              value={filters.shipmentFeedFilter}
              onChange={(e) => filters.setShipmentFeedFilter(e.target.value)}
              sx={{
                width: 200,
                "& .MuiOutlinedInput-root":
                  filters.shipmentFeedFilter && searchParams.get("shipmentFeedId")
                    ? {
                        "& fieldset": {
                          borderColor: "primary.main",
                        },
                      }
                    : {},
              }}
            />
          </Box>

          <Box display="flex">
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.errorsOnly}
                    onChange={(e) => {
                      filters.setErrorsOnly(e.target.checked);
                    }}
                    size="small"
                  />
                }
                label={
                  viewMode === "grouped"
                    ? "Show only emails with errors"
                    : "Show only actions with errors"
                }
              />
              {filters.errorsOnly && (
                <TextField
                  size="small"
                  label="Error Regex Filter (optional)"
                  value={filters.errorRegex}
                  onChange={(e) => filters.setErrorRegex(e.target.value)}
                  placeholder="e.g., timeout|failed|error"
                  sx={{ minWidth: 250 }}
                />
              )}
            </Stack>

            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              flexWrap="wrap"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.includeDescription}
                    onChange={(e) => {
                      filters.setIncludeDescription(e.target.checked);
                    }}
                    size="small"
                  />
                }
                label="Include detailed descriptions"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.displayErrors}
                    onChange={(e) => {
                      filters.setDisplayErrors(e.target.checked);
                    }}
                    size="small"
                  />
                }
                label="Display errors inline"
              />
            </Stack>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Total: {total} {`${viewMode === "grouped" ? "emails" : "records"}`}{" "}
            | Showing: {actionLogsCount} individual log results{" "}
            {viewMode === "grouped" &&
              groupedLogsCount !== undefined &&
              `across ${groupedLogsCount} emails`}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
